import React from 'react';

export default function PrivacyPage() {
  const sectionHeading = {
    color: '#1e293b',
    fontSize: '1.15rem',
    fontWeight: '600',
    marginTop: '1.75rem',
    marginBottom: '0.5rem',
  };

  const paragraph = {
    color: '#475569',
    fontSize: '0.9rem',
    lineHeight: '1.7',
    marginBottom: '0.75rem',
  };

  const list = {
    color: '#475569',
    fontSize: '0.9rem',
    lineHeight: '1.7',
    paddingLeft: '1.25rem',
    marginBottom: '0.75rem',
  };

  const link = {
    color: '#01696F',
  };

  const bold = {
    fontWeight: '600',
    color: '#1e293b',
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '2rem',
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        padding: '2.5rem',
        width: '100%',
        maxWidth: '760px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
        marginTop: '1rem',
        marginBottom: '2rem',
      }}>
        <a href="/" style={{ color: '#01696F', fontSize: '0.85rem', textDecoration: 'none' }}>&larr; Back to home</a>

        {/* Draft Banner */}
        <div style={{
          backgroundColor: '#fef9c3',
          border: '1px solid #facc15',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          marginTop: '1.25rem',
          marginBottom: '0.5rem',
          textAlign: 'center',
          fontSize: '0.85rem',
          fontWeight: '600',
          color: '#854d0e',
        }}>
          DRAFT — Pending external counsel review of arbitration, AADC, and governing-law clauses
        </div>

        <h1 style={{ color: '#1e293b', fontSize: '1.5rem', fontWeight: '700', marginTop: '1.5rem', marginBottom: '0.25rem' }}>Privacy Policy</h1>
        <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '2rem' }}>Effective Date: April 26, 2026</p>

        {/* 1. Who We Are */}
        <h2 style={sectionHeading}>1. Who We Are</h2>
        <p style={paragraph}>
          Bragsheet LLC ("BragSheet," "we," "us," or "our") operates the BragSheet application
          at <a href="https://app.bragsheet.io" style={link}>app.bragsheet.io</a> and the website
          at <a href="https://bragsheet.io" style={link}>bragsheet.io</a>.
        </p>
        <p style={paragraph}>
          Our mailing address is: Bragsheet LLC, 116 Agnes Rd Ste 200, Knoxville, TN 37919, USA.
        </p>
        <p style={paragraph}>
          For privacy inquiries, contact us at <a href="mailto:privacy@bragsheet.io" style={link}>privacy@bragsheet.io</a>.
        </p>

        {/* 2. Information We Collect */}
        <h2 style={sectionHeading}>2. Information We Collect</h2>

        <p style={paragraph}><span style={bold}>Account Information:</span> Name, email address, graduation year, and date of birth (used solely to verify you are at least 13 years old and to age-appropriately configure features). We also record the IP address and browser user agent associated with your account at signup for security and fraud-prevention purposes.</p>

        <p style={paragraph}><span style={bold}>Profile Data:</span> School name, GPA (weighted and unweighted), standardized test scores (SAT, ACT).</p>

        <p style={paragraph}><span style={bold}>Billing Information (Pro subscribers only):</span> If you subscribe to BragSheet Pro, our payment processor Stripe collects and processes your payment method information directly. We do not receive or store full payment card details. We do receive and store a Stripe customer identifier, your subscription status, and billing event metadata (for example, trial start date, plan, and renewal date).</p>

        <p style={paragraph}><span style={bold}>Journal Content:</span> Journal entry titles, content, categories, tags, and dates.</p>

        <p style={paragraph}><span style={bold}>Activities and Awards:</span> Activity names, types, roles, descriptions, hours, and award details you add to your profile.</p>

        <p style={paragraph}>
          <span style={bold}>Authentication Data:</span> We use AWS Cognito to manage your account.
          If you sign in with Google, we receive your name and email from Google. We do not store your password — it is hashed and managed by AWS Cognito. If you enable multi-factor authentication, AWS Cognito stores your MFA shared secret (and recovery codes, if generated) on our behalf.
        </p>

        <p style={paragraph}>
          <span style={bold}>Usage Data:</span> We use Google Analytics to collect anonymous usage data
          including page views, session duration, general location (country level), device type, and
          browser type. Google Analytics uses cookies for this purpose.
        </p>

        <p style={paragraph}>
          <span style={bold}>Technical Data:</span> IP address, browser type, and device information
          collected automatically via server logs.
        </p>

        {/* 3. How We Use Your Information */}
        <h2 style={sectionHeading}>3. How We Use Your Information</h2>
        <ul style={list}>
          <li>To provide, maintain, and improve the BragSheet service</li>
          <li>To authenticate your identity and secure your account</li>
          <li>To generate AI-powered suggestions (essay themes, activity descriptions, auto-tags) using your journal content and profile data</li>
          <li>To send transactional emails (account verification, password reset, security alerts) via AWS SES</li>
          <li>To analyze usage patterns and improve the service via Google Analytics</li>
          <li>To respond to your inquiries via our contact form</li>
          <li>We never sell your personal information to third parties</li>
          <li>We never use your data for targeted advertising</li>
        </ul>

        {/* 4. AI Features and Your Data */}
        <h2 style={sectionHeading}>4. AI Features and Your Data</h2>
        <p style={paragraph}>
          BragSheet uses AI features powered by AWS Bedrock (Anthropic Claude). When you use AI
          features (auto-tagging, essay brainstorm, activity descriptions), your journal content
          and/or profile data is sent to the AI service to generate suggestions.
        </p>
        <p style={paragraph}>
          <span style={bold}>Important:</span> Your data is NOT used to train or fine-tune AI models.
          Our AI provider (Anthropic, via AWS Bedrock) processes your data only to generate responses
          and does not retain it for model training purposes.
        </p>
        <p style={paragraph}>
          AI features are optional. You can use BragSheet as a plain journal without activating any
          AI features. AI-generated suggestions (tags, essay themes, descriptions) are stored only
          within your account alongside the entries they relate to.
        </p>
        <p style={paragraph}>For users under 18, AI features are limited to producing the specific suggestion you requested. We do not use AI to infer personality traits, mental-health status, or other sensitive attributes about minor users, and we do not use AI to drive marketing, recommendations, or any form of behavioral profiling. AI-generated suggestions are not a substitute for advice from teachers, counselors, or licensed mental-health professionals.</p>
        <p style={paragraph}>BragSheet is a journaling tool and is not a mental-health, crisis-intervention, or counseling service. If your entries reflect thoughts of self-harm, harm to others, or a mental-health emergency, BragSheet does not currently provide automated intervention or alerts. If you or someone you know is in crisis, please contact 988 (the US Suicide and Crisis Lifeline), reach out to a trusted adult, school counselor, or licensed mental-health professional, or call your local emergency services.</p>

        {/* 5. Third-Party Services */}
        <h2 style={sectionHeading}>5. Third-Party Services</h2>
        <p style={paragraph}>
          We share data with the following third-party services, solely to provide the BragSheet service:
        </p>

        <div style={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          overflow: 'hidden',
          marginBottom: '1rem',
          fontSize: '0.85rem',
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            backgroundColor: '#f1f5f9',
            fontWeight: '600',
            color: '#1e293b',
            padding: '0.6rem 0.75rem',
            borderBottom: '1px solid #e2e8f0',
          }}>
            <div>Service</div>
            <div>Data Shared</div>
            <div>Purpose</div>
          </div>
          {/* Table Rows */}
          {[
            ['AWS Cognito', 'Email, name, password (hashed)', 'Authentication and account management'],
            ['AWS DynamoDB', 'All user data', 'Encrypted data storage'],
            ['AWS Lambda', 'All user data (in transit)', 'Backend processing'],
            ['AWS SES', 'Email address', 'Transactional email delivery'],
            ['AWS Bedrock (Anthropic Claude)', 'Journal content, profile data', 'AI feature processing'],
            ['Google OAuth (optional)', 'Name, email', 'Social sign-in'],
            ['Stripe', 'Email, name, payment method, Stripe customer ID, subscription status', 'Payment processing and subscription management'],
            ['Google Analytics', 'Anonymous usage data, IP, cookies', 'Usage analytics'],
          ].map(([service, data, purpose], i, arr) => (
            <div key={service} style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              padding: '0.55rem 0.75rem',
              color: '#475569',
              borderBottom: i < arr.length - 1 ? '1px solid #f1f5f9' : 'none',
              lineHeight: '1.5',
            }}>
              <div style={{ fontWeight: '500', color: '#334155' }}>{service}</div>
              <div>{data}</div>
              <div>{purpose}</div>
            </div>
          ))}
        </div>

        <p style={paragraph}>
          Each of these services operates under its own privacy policy. We have Data Processing
          Agreements in place with AWS and Google.
        </p>

        {/* 6. Data Storage and Security */}
        <h2 style={sectionHeading}>6. Data Storage and Security</h2>
        <ul style={list}>
          <li>All data is stored in AWS DynamoDB with encryption at rest</li>
          <li>Data is transmitted exclusively over HTTPS</li>
          <li>Authentication is managed by AWS Cognito with support for multi-factor authentication (TOTP)</li>
          <li>We implement rate limiting, input validation, and security headers to protect against abuse</li>
          <li>Access to production data is restricted to authorized personnel</li>
          <li>We conduct regular security reviews of our infrastructure</li>
        </ul>
        <p style={paragraph}>
          Your data is stored and processed in the United States using Amazon Web Services. If you
          access BragSheet from outside the United States, your information will be transferred to
          and processed in the US.
        </p>

        {/* 7. Data Retention */}
        <h2 style={sectionHeading}>7. Data Retention</h2>
        <ul style={list}>
          <li><span style={bold}>Account data:</span> Retained for the duration of your active account.</li>
          <li><span style={bold}>Journal entries, activities, awards:</span> Retained until you delete them or delete your account.</li>
          <li><span style={bold}>Account deletion:</span> When you delete your account, we permanently delete your profile, journal entries, activities, awards, and your authentication record (including your hashed password and any multi-factor authentication secret) within 30 days. Billing records held by our payment processor Stripe are retained by Stripe under their own retention schedule and as required by applicable tax and financial-recordkeeping laws (typically seven years). You may contact <a href="mailto:privacy@bragsheet.io" style={link}>privacy@bragsheet.io</a> to request anonymization or deletion of Stripe customer records to the extent permitted by law.</li>
          <li><span style={bold}>Backups:</span> Point-in-time recovery backups are purged within 35 days of deletion.</li>
          <li><span style={bold}>Server logs:</span> Retained for up to 12 months for security and debugging purposes.</li>
          <li><span style={bold}>Google Analytics data:</span> Retained per Google's default retention settings.</li>
          <li><span style={bold}>Date of birth:</span> Stored for the life of the account to maintain the 13+ age gate; deleted with the account.</li>
          <li><span style={bold}>Authentication records:</span> Hashed passwords and MFA secrets are managed by AWS Cognito; deleted with the account.</li>
          <li><span style={bold}>Billing records:</span> Stripe retains billing transaction records under its own retention schedule and as required by tax law (typically up to seven years), even after account deletion.</li>
        </ul>

        {/* 8. Your Rights */}
        <h2 style={sectionHeading}>8. Your Rights</h2>
        <p style={paragraph}>
          Depending on your location, you may have the following rights regarding your personal information:
        </p>
        <ul style={list}>
          <li><span style={bold}>Access:</span> View all your data through the app's profile and journal pages, or request a copy by emailing <a href="mailto:privacy@bragsheet.io" style={link}>privacy@bragsheet.io</a>.</li>
          <li><span style={bold}>Export:</span> Download your journal (JSON or CSV) and profile (JSON) at any time from within the app.</li>
          <li><span style={bold}>Correction:</span> Update your profile information at any time through the app.</li>
          <li><span style={bold}>Deletion:</span> Permanently delete your account and all associated data from your Profile page (Danger Zone section), or by emailing <a href="mailto:privacy@bragsheet.io" style={link}>privacy@bragsheet.io</a>.</li>
          <li><span style={bold}>Restrict Processing:</span> You may request that we limit how we process your data by contacting us.</li>
          <li><span style={bold}>Data Portability:</span> Export your data in machine-readable format (JSON, CSV) at any time.</li>
          <li><span style={bold}>Withdraw Consent:</span> You may withdraw consent for non-essential data processing (such as analytics) at any time.</li>
        </ul>
        <p style={paragraph}>
          To exercise any of these rights, contact us at <a href="mailto:privacy@bragsheet.io" style={link}>privacy@bragsheet.io</a>.
          We will respond within 30 days (or as required by applicable law).
        </p>

        {/* 8a. Other US State Privacy Rights */}
        <h2 style={sectionHeading}>8a. Other US State Privacy Rights</h2>
        <p style={paragraph}>If you are a resident of Virginia, Colorado, Connecticut, Utah, Texas, Oregon, or another US state with a comprehensive consumer-privacy law, you may have rights similar to those described above, including the rights to access, correct, delete, and obtain a portable copy of your personal information, and to opt out of targeted advertising, sale of personal information, and certain profiling. To exercise these rights, contact <a href="mailto:privacy@bragsheet.io" style={link}>privacy@bragsheet.io</a>. Where a state law provides an appeal process, you may appeal our decision by replying to our response within 30 days; if we deny your appeal, you may contact your state attorney general.</p>

        {/* 9. California Privacy Rights (CCPA/CPRA) */}
        <h2 style={sectionHeading}>9. California Privacy Rights (CCPA/CPRA)</h2>
        <p style={paragraph}>
          If you are a California resident, you have additional rights under the California Consumer
          Privacy Act and California Privacy Rights Act:
        </p>
        <ul style={list}>
          <li><span style={bold}>Right to Know:</span> You may request the categories and specific pieces of personal information we have collected about you, the sources, the business purposes, and the categories of third parties with whom we share it.</li>
          <li><span style={bold}>Right to Delete:</span> You may request deletion of your personal information, subject to certain exceptions.</li>
          <li><span style={bold}>Right to Correct:</span> You may request correction of inaccurate personal information.</li>
          <li><span style={bold}>Right to Opt-Out of Sale/Sharing:</span> We do not sell or share your personal information as defined under CCPA/CPRA.</li>
          <li><span style={bold}>Right to Limit Use of Sensitive Information:</span> Date of birth, precise age, GPA, test scores, and educational records may be considered sensitive personal information under California law. We use this data only to provide the BragSheet service and do not use it for advertising or profiling.</li>
          <li><span style={bold}>Non-Discrimination:</span> We will not discriminate against you for exercising your privacy rights.</li>
        </ul>
        <p style={paragraph}>
          To exercise these rights, email <a href="mailto:privacy@bragsheet.io" style={link}>privacy@bragsheet.io</a> or
          use the in-app account deletion feature. We will verify your identity before fulfilling
          requests and respond within 45 days.
        </p>

        {/* 10. Children Under 13 */}
        <h2 style={sectionHeading}>10. Children Under 13</h2>
        <p style={paragraph}>
          We do not knowingly collect information from children under 13. If we learn we have collected
          information from a child under 13, we will delete it. Parents who believe their child has
          registered may contact us at <a href="mailto:privacy@bragsheet.io" style={link}>privacy@bragsheet.io</a>.
        </p>
        <p style={paragraph}>
          BragSheet is designed for high school students. You must be at least 13 years old to create an account. We collect your date of birth at signup to enforce this minimum age. Because we do not knowingly collect personal information from children under 13, the Children's Online Privacy Protection Act (COPPA) does not apply to our service in the ordinary course. If we ever learn that a user is under 13, we will promptly delete their account and associated data. We strongly encourage parents or guardians of users between the ages of 13 and 17 to review this Privacy Policy together with the user. Parents and guardians of users under 18 may contact <a href="mailto:privacy@bragsheet.io" style={link}>privacy@bragsheet.io</a> to (a) review their child's personal information, (b) request deletion of their child's data, or (c) refuse further collection of their child's data. We will verify the requester's relationship to the user before acting on such requests.
        </p>

        {/* 11. Student Data Protection */}
        <h2 style={sectionHeading}>11. Student Data Protection</h2>
        <p style={paragraph}>
          BragSheet does not sell or rent personal information of any user, does not engage in targeted advertising or cross-context behavioral advertising, does not build advertising profiles, and does not share user data with advertising networks. For users under 18, we additionally do not engage in any behavioral profiling, do not infer personality, mental-health, or sensitive attributes for any purpose other than providing the specific feature the user requested, and apply data-minimization defaults. These commitments are consistent with the California Age-Appropriate Design Code Act (AADC), the California Student Online Personal Information Protection Act (SOPIPA), the Illinois Student Online Personal Protection Act (SOPPA), and similar state student-privacy and minor-protection laws. BragSheet maintains an internal Data Protection Impact Assessment for features likely to be accessed by users under 18, which we review and update as the service evolves.
        </p>

        {/* 12. Cookies and Local Storage */}
        <h2 style={sectionHeading}>12. Cookies and Local Storage</h2>
        <ul style={list}>
          <li><span style={bold}>Essential:</span> We use browser localStorage to store your authentication token. This is necessary for the app to function.</li>
          <li><span style={bold}>Analytics:</span> We use Google Analytics, which sets cookies to distinguish users and collect anonymous usage data. You may opt out of Google Analytics by rejecting non-essential cookies via our cookie consent banner, or by installing the <a href="https://tools.google.com/dlpage/gaoptout" style={link}>Google Analytics opt-out browser add-on</a>.</li>
          <li>We do not use cookies for advertising or tracking across other websites.</li>
        </ul>

        {/* 13. Data Breach Notification */}
        <h2 style={sectionHeading}>13. Data Breach Notification</h2>
        <p style={paragraph}>
          In the event of a data breach affecting your personal information, we will notify you via
          email and/or in-app notification within 72 hours of becoming aware of the breach (or as
          required by applicable law). The notification will include: a description of the breach,
          the types of data affected, steps we are taking to address it, and steps you can take to
          protect yourself.
        </p>

        {/* 14. International Users */}
        <h2 style={sectionHeading}>14. International Users</h2>
        <p style={paragraph}>
          If you access BragSheet from outside the United States, please be aware that your
          information will be transferred to, stored, and processed in the United States. By using
          BragSheet, you consent to this transfer. For users in the European Economic Area, we rely
          on Standard Contractual Clauses as the legal mechanism for data transfers.
        </p>

        {/* 15. Changes to This Policy */}
        <h2 style={sectionHeading}>15. Changes to This Policy</h2>
        <p style={paragraph}>
          We may update this Privacy Policy from time to time. For minor changes, we will update
          the effective date at the top. For material changes, we will notify you via email and/or
          in-app notification at least 30 days before the changes take effect and request your
          acknowledgment. Your continued use after non-material changes constitutes acceptance.
        </p>

        {/* 16. Contact Us */}
        <h2 style={sectionHeading}>16. Contact Us</h2>
        <p style={paragraph}>For privacy questions, data requests, or concerns:</p>
        <ul style={list}>
          <li><span style={bold}>Email:</span> <a href="mailto:privacy@bragsheet.io" style={link}>privacy@bragsheet.io</a></li>
          <li><span style={bold}>Mail:</span> Bragsheet LLC, 116 Agnes Rd Ste 200, Knoxville, TN 37919, USA</li>
        </ul>
      </div>
    </div>
  );
}
